"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  type AddQuoteResult,
  type DogEntryPage,
  type DogQuote,
  type Item,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ComposedAutoComplete } from "@/components/shared/composedAutoComplete/ComposedAutoComplete";
import { useAutoComplete } from "@/components/shared/composedAutoComplete/hooks/useAutoComplete";
import { ComposedInputField } from "@/components/shared/composedInputField/ComposedInputField";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useDogBreeds } from "@/hooks/api/useDogBreeds";
import { useEntry } from "@/hooks/useEntry";
import {
  type DogEntryFormObject,
  ResolveDogEntry,
} from "@/validators/products/dog/DogEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: DogEntryPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: DogQuote) => Promise<AddQuoteResult>;
};

export const DogEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    content: dogEntryPage,
    isLoading,
    submitHandler,
  } = useEntry<DogQuote, DogEntryPage>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    contentKey: "dogEntryPage",
  });

  const defaultValues: DogQuote = {
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    productType: ProductType.DOG,
    dogCovered: {
      dogName: "",
      dogBreed: "",
    },
  };

  const { handleSubmit, control, formState, watch } =
    useForm<DogEntryFormObject>({
      defaultValues,
      disabled: isLoading,
      resolver: yupResolver(ResolveDogEntry(dogEntryPage)),
    });
  const { errors } = formState;

  const dogBreed = watch("dogCovered.dogBreed");

  const { data: dogBreeds, isLoading: isLoadingDogBreeds } = useDogBreeds();
  const { query, setQuery, suggestions } = useAutoComplete(
    dogBreeds?.items ?? [],
    dogBreed,
    (item: Item) => item?.value ?? "",
    (item: Item) => item?.key ?? "",
    (item: Item) => item?.key ?? "",
  );

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form onSubmit={handleSubmit(submitHandler)}>
          <Controller
            control={control}
            name="dogCovered.dogName"
            render={({ field }) => (
              <ComposedInputField
                id={field.name}
                errorMessage={errors.dogCovered?.dogName?.message}
                disabled={isLoading}
                data-testid="dog-name-input"
                placeholder={
                  dogEntryPage?.dogNameQuestion?.placeholder?.value ?? undefined
                }
                popoverMessage={dogEntryPage?.dogNameQuestion?.popover?.value}
                {...field}
              >
                {dogEntryPage?.dogNameQuestion?.label?.value}
              </ComposedInputField>
            )}
          ></Controller>

          <Controller
            control={control}
            name="dogCovered.dogBreed"
            render={({ field }) => (
              <ComposedAutoComplete
                id={field.name}
                errorMessage={errors.dogCovered?.dogBreed?.message}
                isLoading={isLoadingDogBreeds}
                suggestions={suggestions ?? []}
                dataTestid="dog-breed-input"
                setQuery={setQuery}
                placeholder={
                  dogEntryPage?.dogBreedQuestion?.placeholder?.value ??
                  undefined
                }
                popoverMessage={dogEntryPage?.dogBreedQuestion?.popover?.value}
                onClear={() => {
                  setQuery("");
                  field.onChange?.("");
                }}
                onSuggestionSelected={(suggestion) => {
                  if (!field.onChange || !suggestion) {
                    return;
                  }
                  setQuery(suggestion.label);
                  field.onChange(suggestion?.value?.toString() ?? "");
                }}
                inputValue={query}
                {...field}
                value={field.value ?? undefined}
              >
                {dogEntryPage?.dogBreedQuestion?.label?.value}
              </ComposedAutoComplete>
            )}
          ></Controller>

          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                errorMessage={errors.policyHolderSsn?.message}
                disabled={
                  isLoading ||
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated
                }
                data-testid="policy-holder-ssn-input"
                placeholder={
                  dogEntryPage?.policyHolderSsn?.placeholder?.value ?? undefined
                }
                popoverMessage={dogEntryPage?.policyHolderSsn?.popover?.value}
                {...field}
              >
                {dogEntryPage?.policyHolderSsn?.label?.value}
              </SsnInput>
            )}
          ></Controller>
          <ProductContainer.CallToAction
            htmlType="submit"
            data-testid="dog-entry-submit-button"
            disabled={isLoading}
            loading={isLoading}
          >
            {dogEntryPage?.submitButton?.label?.value}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
